import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, INTUITION, APPROACH, AB, CATCH, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const pulse = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="reranking · what it fixes" color={COLORS.cyan} />
          <Big>
            My #1 result was{" "}
            <span style={{ color: COLORS.cyan }}>95% sure</span>.
            <br />
            And it was the{" "}
            <span style={{ color: COLORS.red, opacity: 0.35 + pulse * 0.65 }}>
              WRONG
            </span>{" "}
            chunk.
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM : the LLM emulating a cross-encoder */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the old reranker · llm emulation" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="amber" title="48 candidate chunks" width={420}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>split into batches</div>
              <div style={{ color: COLORS.amber }}>BATCH_SIZE = 5</div>
              <div style={{ color: COLORS.dim }}>previews, temp 0</div>
            </div>
          </Panel>
          <Panel accent="red" title="one chat-completion / batch" width={520}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 88, color: COLORS.red, lineHeight: 1 }}>
                ~10 calls
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, marginTop: 8 }}>
                per query · gemini-2.5-flash · ≈ 1,100 ms
              </div>
            </div>
          </Panel>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 28 }}>
          a chat model, borrowed to do a scoring model's job
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="amber" />
  </AbsoluteFill>
);

/* 03 — INTUITION : a real cross-encoder */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition · cross-encoder" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 34, alignItems: "center" }}>
          <Big size={58}>(query</Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 52, color: COLORS.violet }}>+</div>
          <Big size={58}>chunk)</Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 52, color: COLORS.dim }}>→</div>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 72, color: COLORS.green }}>
            0.94
          </span>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 30 }}>
          read together · one relevance score · a model built for this
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — APPROACH : Voyage rerank-2.5, one call, flag-gated, fail-open */
const PIPE: PipeNode[] = [
  { label: "pool", value: "48 chunks", accent: "cyan" },
  { label: "provider", value: "RERANK_PROVIDER", sub: "= voyage", accent: "green" },
  { label: "rerank-2.5", value: "1 API call", sub: "cross-encoder", accent: "violet" },
  { label: "on error", value: "FAIL-OPEN", sub: "→ LLM scorer", accent: "amber" },
  { label: "output", value: "RE-RANKED", sub: "top-K", accent: "lime" },
];
export const Scene04Approach: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.approach.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="RERANK_PROVIDER=voyage · rerank-2.5" color={COLORS.green} />
      <div style={{ width: 1700 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={20} nodeWidth={220} />
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 34 }}>
        a one-row DB flag flips it — no deploy. Any failure falls back, silently.
      </div>
    </AbsoluteFill>
    <Caption lines={APPROACH} accent="green" />
  </AbsoluteFill>
);

/* 05 — AB : the real numbers */
export const Scene05AB: React.FC = () => {
  const frame = useCurrentFrame();
  const parity = interpolate(frame, [200, 240], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.ab.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the A/B · clean queries" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.dim }}>
            1,100 ms
          </span>
          <span style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</span>
          <MetricCounter value={484} from={1100} startFrame={10} durationFrames={60} accent="lime" suffix=" ms" fontSize={140} />
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 6 }}>
          8 batched LLM calls → 1 cross-encoder call
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 40, opacity: parity }}>
          <Callout label="quality" accent="green" width={420}>
            same <b style={{ color: COLORS.text }}>#1</b> · recall@3 <b style={{ color: COLORS.text }}>6 / 6</b> — parity, not a win
          </Callout>
          <Callout label="the real win" accent="lime" width={420}>
            <b style={{ color: COLORS.text }}>~2× faster</b>, cheaper, purpose-built
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={AB} accent="lime" />
    </AbsoluteFill>
  );
};

/* 06 — CATCH : fail-open + the truthful instrument */
const SCORER = `// src/lib/rag/reranker.ts — the trace reports the REAL scorer
scorer = crossEncoderScored
  ? { used: 'cross-encoder', provider: 'voyage', calls: 1 }
  : { used: 'llm',
      provider: configuredProvider,      // read AFTER config load
      calls: batches.length,
      failOpenReason }                    // '429' / 'key-missing' / …

// the pipeline step used to HARD-CODE 'LLM' — it lied for weeks.`;
export const Scene06Catch: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.catch.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 30 }}>
      <Kicker text="the honest catch · 429 → fail-open" color={COLORS.amber} />
      <CodePanel
        code={SCORER}
        language="typescript"
        filename="reranker.ts · RerankScorer"
        highlightLines={[6, 7]}
        revealFrames={330}
        fontSize={23}
        width={1260}
      />
    </AbsoluteFill>
    <Caption lines={CATCH} accent="amber" />
  </AbsoluteFill>
);

/* 07 — OUTRO */
export const Scene07Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          The reranker is the last
          <br />
          correction before the answer.
          <br />
          <span style={{ color: COLORS.cyan }}>Make the instrument name which one fired.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · grounded retrieval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
